import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { axiosInstance } from '~/api/config/axios.config';
import { errorHandler } from '~/store/utils/errorHendler';
import { useForm } from 'react-hook-form';
import { Box, Button, Grid, Typography } from '@material-ui/core';
import css from '~/features/dashboard/components/editable-list-drawer/styles.module.scss';
import { TextFieldOutlined } from '~/common/text-field-outlined';
import classNames from 'classnames';
import ListItem from '@material-ui/core/ListItem';
import { VerticalPopver } from '~/features/dashboard/components/vertical-popover';

interface Props {
    title?: string;
    items: ItemType[];
    onSave(item: ItemType, callback?: { (id: number): void }): void;
    onRemove(id: number, callback?: { (id: number): void }): void;
    itemsPerPage?: number;
}
export type ItemType = {
    id: number;
    listingText: string;
};

export type ItemTypeSong = {
    id: number;
    customField: number;
    listingText: string;
};

type listProps = Pick<Props, 'items' | 'itemsPerPage' | 'title'> & {
    //
};

export const EditSynchTab: React.FC<listProps> = ({ title = 'Genre', ...props }) => {
    const dispatch = useDispatch();

    const onSave = (item: ItemType, callback?: { (id: number): void }) => {
        // TODO: use redux actions
        axiosInstance
            .put('/catalogue/projectCreator/userlisting/save', {
                id: item.id,
                listingArea: 'PUBLISHING',
                listingKey: (title === 'Artwork' || title === 'Sound') ? `${title.toUpperCase()}TAG` : `SONG${title.toUpperCase()}`,
                listingCode: item?.listingText,
                listingText: item?.listingText,
            })
            .then(({ data }) => {
                callback && callback(data.id);
            })
            .catch(e => {
                if (e?.response?.data?.ExceptionMessage) {
                    errorHandler(e?.response?.data?.ExceptionMessage, dispatch);
                } else {
                    throw e;
                }
            });
    };

    const onRemove = (id: number, callback?: { (id: number): void }) => {
        // TODO: use redux actions
        axiosInstance.delete(`/catalogue/projectCreator/userlisting/delete/${id}`).then(({ data }) => {
            if (data) {
                callback && callback(id);
            } else {
                errorHandler('Something went wrong', dispatch);
            }
        });
    };

    return <EditableList title={title} {...props} onSave={onSave} onRemove={onRemove} />;
};

export const EditableList: React.FC<Props> = ({ title = 'Items', items: rawItems, onSave, onRemove, itemsPerPage = 10 }) => {
    const [editData, setEditData] = React.useState<ItemType | false>(false);

    const [items, setItems] = useState<ItemType[]>([...rawItems]);

    const save = (item: ItemType) => {
        onSave(item, (id: number): void => {
            if (Number(item.id)) {
                const index = items.findIndex(i => i.id === id);
                if (index > -1) {
                    const tmpItems = [...items];
                    tmpItems.splice(index, 1, item);
                    setItems(tmpItems);
                }
            } else {
                setItems([...items, { id, listingText: item.listingText }]);
            }
            setEditData(false);
        });
    };

    const remove = (id: number) => {
        onRemove(id, (id): void => {
            const index = items.findIndex(i => i.id === id);
            if (index > -1) {
                const tmpItems = [...items];
                tmpItems.splice(index, 1);
                setItems(tmpItems);
            }
        });
    };

    const { handleSubmit, register, errors } = useForm<ItemType>();

    const customFormSubmit = () => {
      handleSubmit(save)();
    };

    if (editData) {
        return (
            <Grid container className={css.module} spacing={3}>
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="space-between">
                        <Typography variant="h6" gutterBottom>
                            {editData.id ? 'Edit' : 'Add New'}
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Box>
                        <input type="hidden" name="id" ref={register} defaultValue={editData.id} />
                        <TextFieldOutlined
                            fullWidth
                            inputRef={register({ required: 'Required' })}
                            name="listingText"
                            variant="filled"
                            label="Title"
                            defaultValue={editData.listingText}
                            error={!!errors?.listingText}
                            helperText={errors?.listingText?.message}
                        />
                        <Box display="flex" mt={4}>
                            <Button
                                variant="contained"
                                color="primary"
                                disableElevation
                                style={{ borderRadius: '8px' }}
                                onClick={customFormSubmit}
                            >
                                {editData.id ? 'Update' : 'Create'}
                            </Button>
                            <Button
                                variant="contained"
                                color="secondary"
                                disableElevation
                                onClick={() => setEditData(false)}
                                style={{ borderRadius: '8px', marginLeft: '1rem' }}
                            >
                                Cancel
                            </Button>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        );
    }

    return (
        <Grid container style={{ width: '100%', marginTop: '12px' }} spacing={3}>
            <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between">
                    <Typography variant="h6" gutterBottom>
                        Edit {title}
                    </Typography>
                    <Button
                        className={css.btnColorGreen}
                        variant="contained"
                        color="secondary"
                        style={{ height: '2rem', borderRadius: '8px' }}
                        onClick={() => {
                            setEditData({ id: 0, listingText: '' });
                        }}
                    >
                        Add new
                    </Button>
                </Box>
            </Grid>
            <Grid item xs={12}>
                {items.length > 0 && (
                    <Box className={classNames(css.dataList)}>
                        {/*{sortItems(items).slice(0, itemsPerPage).map((i: any) => (*/}
                        {items.map((i: any) => (
                            <ListItem key={i.id || i.listingText.replace(' ', '_')} className={css.item}>
                                <Box width="100%" display="flex" flexDirection="column" justifyContent="center">
                                    <Typography variant="subtitle2">{i.listingText}</Typography>
                                </Box>
                                {!!i.customField && (
                                    <VerticalPopver
                                        childClassName={css.editIcon}
                                        editAction={() => setEditData(i)}
                                        deleteAction={() => {
                                            remove(i.id);
                                        }}
                                    />
                                )}
                            </ListItem>
                        ))}
                    </Box>
                )}
            </Grid>
        </Grid>
    );
};
